import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { MessageSquare, Mail, Loader2, Edit2 } from "lucide-react";
import { toast } from "sonner";

const Inquiries = () => {
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyDialog, setReplyDialog] = useState({ open: false, inquiry: null });
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [editPropertyDialog, setEditPropertyDialog] = useState({ open: false, inquiry: null });
  const [newPropertyRef, setNewPropertyRef] = useState("");

  useEffect(() => {
    if (user?.user_id) {
      fetchInquiries();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API}/inquiries`, {
        withCredentials: true
      });
      setInquiries(response.data || []);
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, status) => {
    try {
      await axios.put(`${API}/inquiries/${inquiryId}/status?status=${status}`, {}, {
        withCredentials: true
      });
      fetchInquiries();
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const statusColors = {
    new: "bg-blue-100 text-blue-700",
    contacted: "bg-yellow-100 text-yellow-700",
    replied: "bg-green-100 text-green-700",
    closed: "bg-gray-100 text-gray-700"
  };

  const openReplyDialog = (inquiry) => {
    setReplyDialog({ open: true, inquiry });
    setReplyMessage(`Dear ${inquiry.name},\n\nThank you for your inquiry about our property.\n\n[Your response here]\n\nBest regards`);
  };

  const sendEmailReply = async () => {
    if (!replyDialog.inquiry || !replyMessage.trim()) return;
    
    setSendingReply(true);
    const propertyRef = replyDialog.inquiry.property_ref || replyDialog.inquiry.property_id;
    const propertyName = replyDialog.inquiry.property_title || "Property";
    try {
      await axios.post(`${API}/inquiries/reply`, {
        recipient_email: replyDialog.inquiry.email,
        recipient_name: replyDialog.inquiry.name,
        subject: `Re: Inquiry about ${propertyRef} - ${propertyName}`,
        message: replyMessage,
        property_title: `${propertyRef} - ${propertyName}`,
        inquiry_id: replyDialog.inquiry.inquiry_id
      }, { withCredentials: true });
      
      toast.success("Email sent successfully!");
      setReplyDialog({ open: false, inquiry: null });
      setReplyMessage("");
      fetchInquiries();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send email";
      toast.error(errorMsg);
    } finally {
      setSendingReply(false);
    }
  };

  const updateInquiryProperty = async () => {
    if (!editPropertyDialog.inquiry || !newPropertyRef.trim()) return;
    
    try {
      await axios.put(`${API}/inquiries/${editPropertyDialog.inquiry.inquiry_id}/property?property_id=${encodeURIComponent(newPropertyRef.trim())}`, {}, {
        withCredentials: true
      });
      toast.success("Property reference updated");
      setEditPropertyDialog({ open: false, inquiry: null });
      setNewPropertyRef("");
      fetchInquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update property");
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-serif">Property Inquiries</h1>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-sm p-4 animate-pulse">
              <div className="h-5 bg-muted rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : inquiries.length === 0 ? (
        <div className="text-center py-16 bg-muted rounded-sm">
          <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-serif text-xl mb-2">No Inquiries Yet</h3>
          <p className="text-muted-foreground">Inquiries from your property listings will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map((inquiry) => (
            <div key={inquiry.inquiry_id} className="bg-card border border-border rounded-sm p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium">{inquiry.name}</h3>
                  <p className="text-sm text-muted-foreground">{inquiry.email}</p>
                  {inquiry.phone && <p className="text-sm text-muted-foreground">{inquiry.phone}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Select value={inquiry.status} onValueChange={(v) => updateStatus(inquiry.inquiry_id, v)}>
                    <SelectTrigger className={`w-28 h-8 text-xs ${statusColors[inquiry.status] || statusColors.new}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-sm bg-muted p-3 rounded">{inquiry.message}</p>
              <div className="flex items-center justify-between mt-3">
                <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                  <span className={`font-medium px-2 py-0.5 rounded font-mono ${inquiry.property_missing ? 'bg-red-100 text-red-700' : 'bg-primary/10 text-foreground'}`}>
                    {inquiry.property_ref || inquiry.property_id}
                    {inquiry.property_missing && " ⚠️"}
                  </span>
                  {user?.role === "admin" && (
                    <button 
                      onClick={() => {
                        setEditPropertyDialog({ open: true, inquiry });
                        setNewPropertyRef(inquiry.property_ref || inquiry.property_id);
                      }}
                      className={`p-1 rounded ${inquiry.property_missing ? 'bg-red-100 hover:bg-red-200 text-red-700' : 'hover:bg-muted'}`}
                      title={inquiry.property_missing ? "Fix broken property link" : "Edit property reference"}
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                  )}
                  <span className="mx-2">&#8226;</span>
                  <span>{inquiry.property_title || "Property"}</span>
                  {(user?.role === "admin" || user?.role === "branch_manager") && inquiry.agent_name && (
                    <>
                      <span className="mx-2">&#8226;</span>
                      <span className="text-primary">Agent: {inquiry.agent_name}</span>
                    </>
                  )}
                </div>
                <Button 
                  size="sm" 
                  onClick={() => openReplyDialog(inquiry)}
                  className="h-8 active:scale-95 transition-all"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Reply
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Email Reply Dialog */}
      <Dialog open={replyDialog.open} onOpenChange={(open) => !sendingReply && setReplyDialog({ open, inquiry: open ? replyDialog.inquiry : null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reply to Inquiry</DialogTitle>
          </DialogHeader>
          {replyDialog.inquiry && (
            <div className="space-y-4">
              <div className="bg-muted p-3 rounded-sm text-sm">
                <p><strong>To:</strong> {replyDialog.inquiry.name} ({replyDialog.inquiry.email})</p>
                <p className="mt-1"><strong>Original Message:</strong></p>
                <p className="italic text-muted-foreground">{replyDialog.inquiry.message}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Your Reply</label>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  className="w-full h-40 p-3 border rounded-sm text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Type your reply here..."
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setReplyDialog({ open: false, inquiry: null })}
                  disabled={sendingReply}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={sendEmailReply}
                  disabled={sendingReply || !replyMessage.trim()}
                >
                  {sendingReply ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Property Dialog (Admin only) */}
      <Dialog open={editPropertyDialog.open} onOpenChange={(open) => setEditPropertyDialog({ open, inquiry: open ? editPropertyDialog.inquiry : null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Property Reference</DialogTitle>
          </DialogHeader>
          {editPropertyDialog.inquiry && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Current: <span className="font-mono">{editPropertyDialog.inquiry.property_ref || editPropertyDialog.inquiry.property_id}</span>
              </p>
              <div>
                <label className="text-sm font-medium mb-2 block">New Property Reference (e.g., BBT-947779)</label>
                <input
                  type="text"
                  value={newPropertyRef}
                  onChange={(e) => setNewPropertyRef(e.target.value)}
                  className="w-full p-2 border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter property ref or ID"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditPropertyDialog({ open: false, inquiry: null })}>
                  Cancel
                </Button>
                <Button onClick={updateInquiryProperty} disabled={!newPropertyRef.trim()}>
                  Update
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inquiries;
